import { useState } from "react";
import { ChevronsUpDown, Plus } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import type { Colega } from "../types";
import { ColegaForm } from "./ColegaList";
import { buildColegaPayload, filterColegas } from "./colegaForm";

/** "Name · Inmobiliaria · phone" label, so same-name colegas can be told apart. */
const colegaLabel = (colega: Colega): string =>
  [colega.full_name, colega.brokerage, colega.phone]
    .filter(Boolean)
    .join(" · ");

/**
 * Almikia Epic E2b (TASK-023): the lead form's colega autocomplete. Searches
 * the catalogue by name, phone or inmobiliaria and offers an inline "Crear
 * colega" that reuses the Colegas screen's form and validation; the server
 * stamps `created_by` with the registering user, exactly as on that screen.
 * Only commercial roles ever reach the lead form, and RLS is the authority
 * for who can read the catalogue. The catalogue is fetched once (<= 1K rows)
 * and filtered locally, so typing never issues a request.
 */
export const ColegaPickerInput = ({
  source = "colega_id",
}: {
  source?: string;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { field } = useInput({ source });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const { data, refetch } = useGetList<Colega>("colegas", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "full_name", order: "ASC" },
  });
  const colegas = data ?? [];
  const selected = colegas.find(
    (colega) => String(colega.id) === String(field.value),
  );
  // Inactive colegas stay selectable only when they are the lead's current one.
  const offered = filterColegas(colegas, query).filter(
    (colega) => colega.active || colega === selected,
  );

  const handleCreate = async (
    values: Parameters<typeof buildColegaPayload>[0],
  ) => {
    const { data: created } = await dataProvider.create<Colega>("colegas", {
      data: buildColegaPayload(values),
    });
    notify("crm.colegas.created_notice");
    await refetch();
    field.onChange(created.id);
    setIsCreating(false);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <Label>{translate("resources.deals.fields.colega_id")}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={translate("resources.deals.fields.colega_id")}
            className="justify-between font-normal"
          >
            <span className="truncate">
              {selected
                ? colegaLabel(selected)
                : translate("crm.colegas.picker_placeholder")}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          {isCreating ? (
            <ColegaForm
              onSave={handleCreate}
              onCancel={() => setIsCreating(false)}
            />
          ) : (
            <Command shouldFilter={false}>
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder={translate("crm.colegas.search_placeholder")}
              />
              <CommandList>
                <CommandEmpty>{translate("crm.colegas.no_match")}</CommandEmpty>
                <CommandGroup>
                  {offered.map((colega) => (
                    <CommandItem
                      key={colega.id}
                      value={String(colega.id)}
                      onSelect={() => {
                        field.onChange(colega.id);
                        setOpen(false);
                      }}
                    >
                      {colegaLabel(colega)}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandGroup>
                  <CommandItem
                    value="create-colega"
                    onSelect={() => setIsCreating(true)}
                  >
                    <Plus className="h-4 w-4" />
                    {translate("crm.colegas.create_action")}
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
